import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { IconName } from '@optimus-web/core';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { isEnterpriseAnalyticsIntegrationEnabled } from '@mews-commander/legacy-entities/src/ducks/enterpriseIntegrations';
import { canCurrentProfileViewSensitiveReports } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import AnalyticsType from '../../powerBi/enums/analyticsType';
import { mapAnalyticsTypeTextKey } from '../../powerBi';
import { createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';

interface AnalyticsMenuProps {
    enterpriseId: string;
    value: string;
}

const AnalyticsMenu = ({ enterpriseId, ...other }: AnalyticsMenuProps) => {
    const canViewSensitiveReports = useCommanderSelector<boolean>(canCurrentProfileViewSensitiveReports);
    const isEnabled = useCommanderSelector<boolean>((state) =>
        isEnterpriseAnalyticsIntegrationEnabled(state, { enterpriseId })
    );
    const isMewsAnalyticsAvailable = canViewSensitiveReports && isEnabled;
    const agingBalanceReportEnabled = useFeatureFlag(FeatureFlag.agingBalanceReportEnabled);
    const depositLedgerReportEnabled = useFeatureFlag(FeatureFlag.depositLedgerReportEnabled);
    const guestLedgerReportEnabled = useFeatureFlag(FeatureFlag.guestLedgerReportEnabled);

    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={IconName.Analytics} />}
            primaryText={<LocalizedText textKey={TextKey.Analytics} />}
            {...other}
        >
            {isMewsAnalyticsAvailable && (
                <>
                    {createListItemWithLink({
                        textKey: mapAnalyticsTypeTextKey[AnalyticsType.Manager],
                        iconName: IconName.ManagerReport,
                        path: 'Analytics/Index',
                        search: { type: AnalyticsType.Manager },
                    })}
                    {agingBalanceReportEnabled &&
                        createListItemWithLink({
                            textKey: mapAnalyticsTypeTextKey[AnalyticsType.AgingBalance],
                            iconName: IconName.RateManagement,
                            path: 'Analytics/Index',
                            search: { type: AnalyticsType.AgingBalance },
                        })}
                    {depositLedgerReportEnabled &&
                        createListItemWithLink({
                            textKey: mapAnalyticsTypeTextKey[AnalyticsType.DepositLedger],
                            iconName: IconName.ExecuteMerchant,
                            path: 'Analytics/Index',
                            search: { type: AnalyticsType.DepositLedger },
                        })}
                    {guestLedgerReportEnabled &&
                        createListItemWithLink({
                            textKey: mapAnalyticsTypeTextKey[AnalyticsType.GuestLedger],
                            iconName: IconName.CustomerProfiles,
                            path: 'Analytics/Index',
                            search: { type: AnalyticsType.GuestLedger },
                        })}
                    {createListItemWithLink({
                        textKey: mapAnalyticsTypeTextKey[AnalyticsType.TrialBalance],
                        iconName: IconName.LegalEnvironments,
                        path: 'Analytics/Index',
                        search: { type: AnalyticsType.TrialBalance },
                    })}
                    {createListItemWithLink({
                        textKey: mapAnalyticsTypeTextKey[AnalyticsType.Financial],
                        iconName: IconName.Finance,
                        path: 'Analytics/Index',
                        search: { type: AnalyticsType.Financial },
                    })}
                    {createListItemWithLink({
                        textKey: mapAnalyticsTypeTextKey[AnalyticsType.Outlet],
                        iconName: IconName.Items,
                        path: 'Analytics/Index',
                        search: { type: AnalyticsType.Outlet },
                    })}
                    {createListItemWithLink({
                        textKey: mapAnalyticsTypeTextKey[AnalyticsType.Pickup],
                        iconName: IconName.Reservations,
                        path: 'Analytics/Index',
                        search: { type: AnalyticsType.Pickup },
                    })}
                </>
            )}
        </SidebarExpandableListItem>
    );
};

export default AnalyticsMenu;
